import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useLocalStorage, useLocalStorageValueCallback} from '@aboutyou/storage';
import {
  doAsync,
  localStorageGetParsedItem,
  localStorageSetItem,
} from '@aboutyou/utils';
import {startTransition, useEffect, useMemo} from 'react';
import {useProductIdFromSlug} from '../ProductUrl/useProductIdFromSlug';

const MAX_LAST_SEEN = 20;
const STORAGE_KEY = 'last_seen_product_ids';

type LastSeenProductStored = {
  id: number;
  seenAt?: string;
};

type LastSeenProduct = {
  id: number;
  lastSeenAt?: Date;
};

/**
 * Migrate old storage format to new one.
 * NOTE: This code can be removed after a few months
 * TODO: https://aboutyou.atlassian.net/browse/ASFE-13567
 */
if (process.browser) {
  const persisted = localStorageGetParsedItem(STORAGE_KEY);
  if (
    Array.isArray(persisted) &&
    persisted.every(id => typeof id === 'number')
  ) {
    localStorageSetItem(
      STORAGE_KEY,
      JSON.stringify(persisted.map((id: number) => ({id}))),
    );
  }
}

/**
 * Returns an array of the last seen product id's, but returns always the same value based on the page history. Therefore, on history back, the result will be the same as it was before. This is usually avoid doing new network requests and show more loading spinners and therefore finally avoids cumulative layout shifts issues.
 */
const useStaleLastSeenProducts = ({
  uniqueKey,
}: {
  uniqueKey: string;
}): LastSeenProduct[] => {
  const lastSeenProducts = useFrequentlyUpdatingLastSeenProducts(
    onInitialSyncFromStorage,
  );
  const [staleLastSeenProducts, updateStaleLastSeenProducts] =
    useHistoryPageState<LastSeenProductStored[]>(lastSeenProducts, {
      uniqueKey,
    });

  // update once the initial sync as the on initial mount `lastSeenProducts` will always be empty. After the hydration updates it will fill data from local storage. This has to be reflected once in the `useHistoryPageState`.
  function onInitialSyncFromStorage(
    nextLastSeenProducts: LastSeenProductStored[],
  ) {
    updateStaleLastSeenProducts(nextLastSeenProducts);
  }

  const productsWithTime = useMemo<LastSeenProduct[]>(
    () =>
      staleLastSeenProducts.map(product => ({
        id: product.id,
        lastSeenAt: product.seenAt ? new Date(product.seenAt) : undefined,
      })),
    [staleLastSeenProducts],
  );

  return productsWithTime;
};

export const useStaleLastSeenProductIds = ({
  uniqueKey,
}: {
  uniqueKey: string;
}) => {
  const staleProducts = useStaleLastSeenProducts({uniqueKey});
  return useMemo(
    () => staleProducts.map(product => product.id),
    [staleProducts],
  );
};

/**
 * Returns the currently last seen product ids. This value will not be save to be used with history back and will most probably introduce cumulative layout shifts and more requests to the backend if used in combination with a data fetcher. It's recommended to use `useStaleLastSeenProductIds` instead for most cases.
 */
export const useFrequentlyUpdatingLastSeenProducts = (
  onInitialSyncFromStorage?: (nextValue: LastSeenProductStored[]) => void,
): LastSeenProduct[] => {
  const [lastSeenProducts = []] = useLocalStorage<LastSeenProductStored[]>({
    key: STORAGE_KEY,
    defaultValue: [],
    ignoreUpdatesOutsideCurrentTab: true,
    onInitialSyncFromStorage,
  });

  const productsWithTime = useMemo(
    () =>
      lastSeenProducts.map(product => ({
        id: product.id,
        lastSeenAt: product.seenAt ? new Date(product.seenAt) : undefined,
      })),
    [lastSeenProducts],
  );

  return productsWithTime;
};

export const useFrequentlyUpdatingLastSeenProductIds = (
  onInitialSyncFromStorage?: (nextValue: LastSeenProductStored[]) => void,
) => {
  const products = useFrequentlyUpdatingLastSeenProducts(
    onInitialSyncFromStorage,
  );
  return useMemo(() => products.map(product => product.id), [products]);
};

const useUpdateLastSeenProductIds = () => {
  const productId = useProductIdFromSlug();

  const getLastSeenProducts = useLocalStorageValueCallback<
    LastSeenProductStored[]
  >({
    key: STORAGE_KEY,
    defaultValue: [],
  });
  const [, setLastSeenProducts] = useLocalStorage<LastSeenProductStored[]>({
    key: STORAGE_KEY,
    defaultValue: [],
    ignoreUpdatesOutsideCurrentTab: true,
  });

  useEffect(() => {
    return doAsync(() => {
      if (productId) {
        const lastSeenProducts = getLastSeenProducts();
        const [firstProduct] = lastSeenProducts || [];
        if (firstProduct?.id !== productId) {
          const newEntry = {id: productId, seenAt: new Date().toISOString()};
          const nextLastSeenProducts = lastSeenProducts
            ? [newEntry, ...lastSeenProducts]
            : [newEntry];

          // deduplicate while maintaining order
          const newProducts: LastSeenProductStored[] = [];
          const seenIds = new Set<number>();
          for (const product of nextLastSeenProducts) {
            if (!seenIds.has(product.id)) {
              seenIds.add(product.id);
              newProducts.push(product);
            }
          }
          startTransition(() => {
            // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
            setLastSeenProducts(newProducts.slice(0, MAX_LAST_SEEN));
          });
        }
      }
    });
  }, [productId, getLastSeenProducts, setLastSeenProducts]);
};

export const LastSeenProductIdsUpdater = () => {
  useUpdateLastSeenProductIds();
  return null;
};
